import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { track } from "../../lib/analytics";
import { startIntentionJourney, streamIntentionResult } from "./intentionApi";

const STORAGE_KEY = "humn-intention-journey-v1";
const DEVICE_KEY = "humn-intention-device-v1";
const EMPTY = {
  stage: 0,
  startedAt: null,
  session: null,
  signals: { worlds: [], pulls: [], energy: {}, people: [], futures: [], sequence: [] },
  result: null,
  intentionId: null,
  reward: null,
};

const JourneyContext = createContext(null);

const readJourney = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
    return saved?.version === 1 ? { ...EMPTY, ...saved.journey } : EMPTY;
  } catch {
    return EMPTY;
  }
};

const getDeviceId = () => {
  let id = localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem(DEVICE_KEY, id);
  }
  return id;
};

export const JourneyProvider = ({ children }) => {
  const [journey, setJourney] = useState(readJourney);
  const [working, setWorking] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: 1, journey }));
  }, [journey]);

  const begin = useCallback(async () => {
    setWorking(true); setError("");
    try {
      const session = journey.session || await startIntentionJourney(getDeviceId());
      setJourney((current) => ({ ...current, session, stage: Math.max(1, current.stage), startedAt: current.startedAt || Date.now() }));
      track("journey_started");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setWorking(false);
    }
  }, [journey.session]);

  const completeStage = useCallback((signalKey, value) => {
    setJourney((current) => {
      const completed = current.stage;
      track(`stage_${completed}_completed`, { duration_seconds: Math.round((Date.now() - current.startedAt) / 1000) });
      return {
        ...current,
        stage: Math.min(6, completed + 1),
        signals: {
          ...current.signals,
          [signalKey]: value,
          sequence: [...current.signals.sequence, `${signalKey}:${Array.isArray(value) ? value.map((item) => item.key || item).join("|") : "complete"}`].slice(-60),
        },
      };
    });
  }, []);

  const generate = useCallback(async (adjustedSignals) => {
    if (!journey.session) return;
    setWorking(true); setError(""); setStatus("Gathering the light you left behind…");
    const signals = adjustedSignals || journey.signals;
    try {
      const event = await streamIntentionResult({
        session_id: journey.session.session_id,
        session_token: journey.session.session_token,
        signals: {
          ...signals,
          duration_seconds: Math.max(1, Math.round((Date.now() - journey.startedAt) / 1000)),
        },
      }, setStatus);
      setJourney((current) => ({ ...current, stage: 7, signals, result: event.result, intentionId: event.intention_id }));
      track("stage_6_completed");
      track("stage_7_completed");
      track("intention_generated", { cached: event.cached });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setWorking(false); setStatus("");
    }
  }, [journey.session, journey.signals, journey.startedAt]);

  const setReward = useCallback((reward) => setJourney((current) => ({ ...current, reward })), []);
  const reset = useCallback(() => { localStorage.removeItem(STORAGE_KEY); setJourney(EMPTY); setError(""); setStatus(""); }, []);

  const value = useMemo(() => ({ journey, working, status, error, setError, begin, completeStage, generate, setReward, reset }), [begin, completeStage, error, generate, journey, reset, setReward, status, working]);
  return <JourneyContext.Provider value={value}>{children}</JourneyContext.Provider>;
};

export const useJourney = () => {
  const context = useContext(JourneyContext);
  if (!context) throw new Error("useJourney must be used inside JourneyProvider");
  return context;
};